import LeaseRecordInput from 'src/components/leases/LeaseRecordInput';
import React, { useEffect, useRef, useState } from 'react';
import useFetch from 'use-http';
import { FixedSizeList as List, ListChildComponentProps } from 'react-window';
import { X, Loader2, Search } from 'src/components/common/icons';
import { ILeaseTin } from './Maintenance1099Modal';

interface IProps {
    isOpen: boolean;
    onClose: () => void;
    onSelect: (tinId: number) => void;
}

const MIN_TIN_LENGTH = 3;
const RESULT_LIST_WIDTH = 1080;
const RESULT_ROW_HEIGHT = 49;
const RESULT_GRID_COLUMNS = '120px minmax(200px, 1fr) 80px 70px 110px 70px 110px 110px 80px';

const ScanByTinModal: React.FC<IProps> = ({ isOpen, onClose, onSelect }) => {
    const { get, response } = useFetch();
    const [scanTinInput, setScanTinInput] = useState('');
    const [scanTaxYearInput, setScanTaxYearInput] = useState('');
    const [scanResults, setScanResults] = useState<ILeaseTin[]>([]);
    const [isScanning, setIsScanning] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);
    const [message, setMessage] = useState('');
    const requestIdRef = useRef(0);

    useEffect(() => {
        if (!isOpen) {
            requestIdRef.current += 1;
            setScanTinInput('');
            setScanTaxYearInput('');
            setScanResults([]);
            setIsScanning(false);
            setHasSearched(false);
            setMessage('');
        }
    }, [isOpen]);

    const handleSearch = async (): Promise<void> => {
        const trimmedTin = scanTinInput.trim();
        const trimmedYear = scanTaxYearInput.trim();

        if (trimmedTin.length < MIN_TIN_LENGTH) {
            setMessage(`Enter at least ${MIN_TIN_LENGTH} TIN characters before searching.`);
            return;
        }

        const requestId = requestIdRef.current + 1;
        requestIdRef.current = requestId;
        setIsScanning(true);
        setHasSearched(true);
        setMessage('');

        try {
            let url = `/v1.0/leases/tins/scan-by-tin?tin=${encodeURIComponent(trimmedTin)}`;
            if (trimmedYear) {
                url += `&taxYear=${encodeURIComponent(trimmedYear)}`;
            }

            const results = await get(url);
            if (requestId !== requestIdRef.current) {
                return;
            }

            if (response.ok && Array.isArray(results)) {
                setScanResults(results as ILeaseTin[]);
            } else {
                setScanResults([]);
                setMessage('The TIN search could not be completed. Please try again.');
            }
        } catch {
            if (requestId === requestIdRef.current) {
                setScanResults([]);
                setMessage('The TIN search could not be completed. Please try again.');
            }
        } finally {
            if (requestId === requestIdRef.current) {
                setIsScanning(false);
            }
        }
    };

    const handleInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
        if (event.key === 'Enter' && !isScanning) {
            event.preventDefault();
            handleSearch();
        }
    };

    const formatMoney = (value?: number | null): string =>
        value !== null && value !== undefined ? value.toLocaleString('en-US', { style: 'currency', currency: 'USD' }) : '';
    const formatDate = (value?: string | null): string => (value ? new Date(value).toLocaleDateString() : '');

    const ResultRow = ({ index, style }: ListChildComponentProps): React.ReactElement => {
        const row = scanResults[index];
        return (
            <div style={{ ...style, ...styles.resultRow, gridTemplateColumns: RESULT_GRID_COLUMNS }}>
                <div style={{ ...styles.tableDataCell, fontWeight: 600, color: '#4F46E5' }}>{row.tin}</div>
                <div style={styles.tableDataCell}>{row.name1}</div>
                <div style={{ ...styles.tableDataCell, textAlign: 'center' }}>{row.taxYear}</div>
                <div style={styles.tableDataCell}>{row.tinType}</div>
                <div style={{ ...styles.tableDataCell, fontWeight: 600, textAlign: 'right' }}>{formatMoney(row.amount)}</div>
                <div style={{ ...styles.tableDataCell, textAlign: 'center' }}>{row.leasesCount || 0}</div>
                <div style={styles.tableDataCell}>{formatDate(row.transmitDate)}</div>
                <div style={styles.tableDataCell}>{formatDate(row.correctDate)}</div>
                <div style={{ ...styles.tableDataCell, textAlign: 'center' }}>
                    <button onClick={() => onSelect(row.tinId)} style={styles.selectButton}>
                        Open
                    </button>
                </div>
            </div>
        );
    };

    if (!isOpen) {
        return null;
    }

    const listHeight = Math.min(500, Math.max(280, window.innerHeight * 0.8 - 190));
    const emptyMessage = message
        ? message
        : isScanning
        ? 'Searching...'
        : hasSearched
        ? 'No matches found.'
        : `Enter at least ${MIN_TIN_LENGTH} TIN characters, then select Search.`;

    return (
        <div
            style={styles.subModalOverlay}
            onClick={(event) => {
                event.stopPropagation();
                onClose();
            }}
        >
            <div style={styles.subModalContent} onClick={(event) => event.stopPropagation()}>
                <div style={styles.modalHeader}>
                    <h3 style={styles.modalTitle}>Scan by TIN</h3>
                    <button onClick={onClose} style={styles.closeButton} aria-label="Close Scan by TIN">
                        <X style={styles.closeIcon} />
                    </button>
                </div>
                <div style={styles.modalBody}>
                    <div role="search" aria-label="TIN search" style={styles.inputRow}>
                        <label htmlFor="scan-tin-input" style={styles.label}>
                            TIN
                        </label>
                        <div style={{ position: 'relative', width: '250px' }}>
                            <Search style={styles.searchIcon} />
                            <LeaseRecordInput
                                id="scan-tin-input"
                                style={styles.inputWithIcon}
                                value={scanTinInput}
                                onChange={(event) => {
                                    setScanTinInput(event.target.value);
                                    setMessage('');
                                }}
                                onKeyDown={handleInputKeyDown}
                                placeholder="Enter TIN characters..."
                                autoFocus={true}
                                disabled={isScanning}
                            />
                        </div>

                        <label htmlFor="scan-tin-tax-year-input" style={{ ...styles.label, marginLeft: '1rem' }}>
                            Tax Year
                        </label>
                        <LeaseRecordInput
                            id="scan-tin-tax-year-input"
                            style={{ ...styles.inputWithIcon, paddingLeft: '0.75rem', width: '120px' }}
                            type="number"
                            value={scanTaxYearInput}
                            onChange={(event) => {
                                setScanTaxYearInput(event.target.value);
                                setMessage('');
                            }}
                            onKeyDown={handleInputKeyDown}
                            placeholder="Optional"
                            disabled={isScanning}
                        />

                        <button type="button" onClick={handleSearch} disabled={isScanning} style={styles.searchButton}>
                            {isScanning ? <Loader2 className="animate-spin" style={styles.loaderIcon} /> : <Search style={styles.buttonIcon} />}
                            {isScanning ? 'Searching...' : 'Search'}
                        </button>
                    </div>

                    <div style={styles.tableWrapper}>
                        <div style={{ width: `${RESULT_LIST_WIDTH}px` }}>
                            <div style={{ ...styles.tableHeader, gridTemplateColumns: RESULT_GRID_COLUMNS }}>
                                <div style={styles.tableHeaderCell}>TIN</div>
                                <div style={styles.tableHeaderCell}>Name</div>
                                <div style={{ ...styles.tableHeaderCell, textAlign: 'center' }}>Tax Year</div>
                                <div style={styles.tableHeaderCell}>Type</div>
                                <div style={{ ...styles.tableHeaderCell, textAlign: 'right' }}>Amount</div>
                                <div style={{ ...styles.tableHeaderCell, textAlign: 'center' }}>Leases</div>
                                <div style={styles.tableHeaderCell}>Trans. Date</div>
                                <div style={styles.tableHeaderCell}>Correct Date</div>
                                <div style={{ ...styles.tableHeaderCell, textAlign: 'center' }}>Action</div>
                            </div>
                            {scanResults.length === 0 ? (
                                <div role="status" aria-live="polite" style={{ ...styles.noResults, height: `${listHeight}px` }}>
                                    {emptyMessage}
                                </div>
                            ) : (
                                <List
                                    height={listHeight}
                                    itemCount={scanResults.length}
                                    itemKey={(index) => scanResults[index].tinId}
                                    itemSize={RESULT_ROW_HEIGHT}
                                    overscanCount={5}
                                    width={RESULT_LIST_WIDTH}
                                >
                                    {ResultRow}
                                </List>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

const styles: { [key: string]: React.CSSProperties } = {
    subModalOverlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(255, 255, 255, 0.8)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 60,
        backdropFilter: 'blur(2px)',
    },
    subModalContent: {
        backgroundColor: '#FFFFFF',
        borderRadius: '0.5rem',
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.2), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
        width: 'min(1150px, 95vw)',
        height: '80vh',
        border: '1px solid #E5E7EB',
        display: 'flex',
        flexDirection: 'column',
    },
    modalHeader: {
        padding: '1rem 1.5rem',
        borderBottom: '1px solid #E5E7EB',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        backgroundColor: '#F9FAFB',
    },
    modalTitle: { margin: 0, fontSize: '1rem', color: '#111827', fontWeight: 600 },
    closeButton: {
        color: '#9CA3AF',
        padding: '0.25rem',
        borderRadius: '50%',
        backgroundColor: 'transparent',
        border: 'none',
        cursor: 'pointer',
    },
    closeIcon: { width: '1rem', height: '1rem' },
    modalBody: { padding: '1.5rem', display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 },
    inputRow: { display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1.5rem', flexWrap: 'wrap' },
    label: { fontSize: '0.875rem', color: '#374151', fontWeight: 500, whiteSpace: 'nowrap' },
    inputWithIcon: {
        padding: '0.5rem 0.75rem',
        paddingLeft: '2rem',
        border: '1px solid #D1D5DB',
        borderRadius: '0.375rem',
        fontSize: '0.875rem',
        color: '#111827',
        backgroundColor: '#FFFFFF',
        outline: 'none',
        width: '100%',
        boxSizing: 'border-box',
    },
    searchIcon: {
        position: 'absolute',
        left: '0.5rem',
        top: '50%',
        transform: 'translateY(-50%)',
        width: '1rem',
        height: '1rem',
        color: '#9CA3AF',
    },
    searchButton: {
        minHeight: '34px',
        padding: '0.45rem 0.85rem',
        border: 'none',
        borderRadius: '6px',
        backgroundColor: '#1E40AF',
        color: '#FFFFFF',
        cursor: 'pointer',
        fontWeight: 600,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '0.4rem',
    },
    loaderIcon: { width: '1rem', height: '1rem', color: '#FFFFFF' },
    buttonIcon: { width: '1rem', height: '1rem' },
    tableWrapper: {
        overflow: 'auto',
        flex: 1,
        minHeight: 0,
        borderRadius: '0.5rem',
        border: '1px solid #E5E7EB',
        backgroundColor: 'white',
    },
    tableHeader: {
        display: 'grid',
        backgroundColor: '#F9FAFB',
        borderBottom: '1px solid #E5E7EB',
    },
    tableHeaderCell: {
        padding: '0.75rem 1rem',
        textAlign: 'left',
        fontSize: '0.75rem',
        fontWeight: 500,
        color: '#6B7280',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    resultRow: {
        display: 'grid',
        borderBottom: '1px solid #F3F4F6',
        boxSizing: 'border-box',
        backgroundColor: '#FFFFFF',
    },
    tableDataCell: {
        padding: '0.75rem 1rem',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        fontSize: '0.875rem',
        color: '#111827',
        boxSizing: 'border-box',
    },
    selectButton: {
        backgroundColor: '#EFF6FF',
        color: '#4F46E5',
        border: '1px solid #BFDBFE',
        padding: '0.25rem 0.75rem',
        borderRadius: '4px',
        fontSize: '0.75rem',
        fontWeight: 600,
        cursor: 'pointer',
    },
    noResults: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1.5rem',
        textAlign: 'center',
        color: '#6B7280',
        fontSize: '0.875rem',
        boxSizing: 'border-box',
    },
};

export default ScanByTinModal;



// WEBPACK FOOTER //
// ./src/components/leases/sections/ScanByTinModal.tsx